import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, AlertCircle, Info, RefreshCw, ShieldCheck } from 'lucide-react';
import { getDebugInfo, authAPI } from '@/lib/api';

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captcha, setCaptcha] = useState(null); // { captcha_id, image }
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  // 2FA flow: 'credentials' -> 'confirm_email' -> 'otp'
  const [step, setStep] = useState('credentials');
  const [pendingToken, setPendingToken] = useState('');
  const [twoFaEmail, setTwoFaEmail] = useState('');
  const [requiredDomain, setRequiredDomain] = useState('bhpl.co.in');
  const [otp, setOtp] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpDeadline, setOtpDeadline] = useState(0);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');

  const openForgot = () => {
    setPendingMessage('');
    setErrorDetails(null);
    setResetCode('');
    setNewPassword('');
    setNewPassword2('');
    setStep('forgot_request');
  };

  const backToLogin = () => {
    setStep('credentials');
    setPendingMessage('');
    setErrorDetails(null);
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      const res = await authAPI.forgotPassword(email.trim().toLowerCase());
      setMaskedEmail(res.data.email_masked || '');
      toast.success('If the account exists, a reset code has been emailed');
      setStep('forgot_reset');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start password reset');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!resetCode.trim()) { toast.error('Enter the reset code from your email'); return; }
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== newPassword2) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword(email.trim().toLowerCase(), resetCode.trim(), newPassword);
      toast.success('Password reset successful — please sign in');
      setPassword('');
      backToLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Password reset failed');
    } finally { setLoading(false); }
  };
  const { login, verifyOtp, register } = useAuth();
  const navigate = useNavigate();

  const resetToCredentials = () => {
    setStep('credentials'); setPendingToken(''); setOtp('');
    setTwoFaEmail(''); setMaskedEmail(''); setErrorDetails(null);
  };

  const loadCaptcha = async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer('');
    try {
      const res = await authAPI.getCaptcha();
      setCaptcha(res.data);
      setShowCaptcha(true);
    } catch {
      /* non-fatal */
    } finally {
      setCaptchaLoading(false);
    }
  };

  // Log debug info on mount
  useEffect(() => {
    const debugInfo = getDebugInfo();
    console.log('[LoginPage] Debug Info:', debugInfo);
  }, []);

  // Live OTP expiry countdown
  useEffect(() => {
    if (step !== 'otp') return;
    const tick = () => setOtpSecondsLeft(Math.max(0, Math.round((otpDeadline - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [step, otpDeadline]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setPendingMessage('');
    setErrorDetails(null);
    
    try {
      if (isLogin) {
        console.log('[LoginPage] Attempting login...');
        const res = await login(email, password, showCaptcha ? { captcha_id: captcha?.captcha_id, captcha_answer: captchaAnswer } : {});
        if (res?.step === 'confirm_email') {
          setPendingToken(res.pending_token);
          setTwoFaEmail((res.current_email || '').toLowerCase());
          setRequiredDomain(res.required_domain || 'bhpl.co.in');
          setStep('confirm_email');
        } else if (res?.step === 'otp') {
          setPendingToken(res.pending_token);
          setMaskedEmail(res.email_masked || '');
          setOtpDeadline(Date.now() + (res.expires_in || 300) * 1000);
          setStep('otp');
        } else {
          // Direct session (no 2FA) — shouldn't happen with 2FA on, but handle gracefully
          toast.success('Logged in successfully');
          navigate('/');
        }
      } else {
        console.log('[LoginPage] Attempting registration...');
        const result = await register(name, email, password);
        if (result.pending) {
          setPendingMessage(result.message);
          toast.info('Registration submitted for approval');
        } else {
          toast.success('Account created successfully');
          navigate('/');
        }
      }
    } catch (err) {
      console.error('[LoginPage] Auth error:', err);

      const rawDetail = err.response?.data?.detail;
      // Backend sends structured detail for login errors: { code, message, captcha_required }
      const detailObj = (rawDetail && typeof rawDetail === 'object') ? rawDetail : null;
      const errorMessage = detailObj?.message
        || err.userMessage
        || (typeof rawDetail === 'string' ? rawDetail : null)
        || 'Authentication failed';

      // Show / refresh CAPTCHA when the backend flags it (after a failed attempt)
      if (isLogin && (detailObj?.code === 'captcha_required' || detailObj?.captcha_required)) {
        await loadCaptcha();
      }

      const debugInfo = err.debugInfo || {
        message: err.message,
        status: err.response?.status,
        url: err.config?.url,
        baseURL: err.config?.baseURL
      };

      setErrorDetails({ message: errorMessage, debug: debugInfo });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorDetails(null);
    try {
      const res = await authAPI.confirmEmail({ pending_token: pendingToken, email: twoFaEmail.trim().toLowerCase() });
      setMaskedEmail(res.data.email_masked || '');
      setStep('otp');
      toast.success('Verification code sent to your email');
    } catch (err) {
      const msg = err.response?.data?.detail || err.userMessage || 'Could not confirm email';
      setErrorDetails({ message: typeof msg === 'string' ? msg : 'Could not confirm email' });
      toast.error(typeof msg === 'string' ? msg : 'Could not confirm email');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorDetails(null);
    try {
      await verifyOtp(pendingToken, otp.trim());
      toast.success('Logged in successfully');
      navigate('/');
    } catch (err) {
      const raw = err.response?.data?.detail;
      const msg = typeof raw === 'string' ? raw : (raw?.message || err.userMessage || 'Invalid code');
      if (err.response?.status === 401) { toast.error('Session expired. Please log in again.'); resetToCredentials(); }
      else { setErrorDetails({ message: msg }); toast.error(msg); }
    } finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    try {
      const res = await authAPI.resendOtp({ pending_token: pendingToken });
      setMaskedEmail(res.data.email_masked || maskedEmail);
      setOtpDeadline(Date.now() + (res.data.expires_in || 300) * 1000);
      toast.success('A new code has been sent');
    } catch (err) {
      const raw = err.response?.data?.detail;
      toast.error(typeof raw === 'string' ? raw : 'Could not resend code');
    }
  };

  const debugInfo = getDebugInfo();

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 relative overflow-hidden">
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1742415106102-77bbfe14b872?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxibHVlcHJpbnQlMjBlbmdpbmVlcmluZyUyMGRyYXdpbmd8ZW58MHx8fHwxNzcwNTI3NDMzfDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <Card className="w-full max-w-md mx-4 shadow-sm border border-slate-200 relative z-10" data-testid="auth-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4">
            <img src="https://customer-assets.emergentagent.com/job_calibration-command/artifacts/0iqwnjwv_bhpl_logo.png" alt="BHPL" className="w-14 h-14 object-contain mx-auto" />
          </div>
          <CardTitle className="text-xl font-bold tracking-tight text-[#0F172A] flex items-center justify-center gap-2">
            BHPL-ERP/SM
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#0F172A] text-white px-1.5 py-0.5 rounded">Sheet Metal</span>
          </CardTitle>
          <CardDescription className="text-slate-500 text-sm">
            {isLogin ? 'Sign in to your account' : 'Create a new account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLogin && step === 'confirm_email' && (
            <form onSubmit={handleConfirmEmail} className="space-y-4" data-testid="confirm-email-form">
              <p className="text-sm text-slate-600">
                For security, please confirm the email where your login code will be sent.
                It must be a <strong>@{requiredDomain}</strong> address.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Notification email</Label>
                <Input
                  type="email"
                  data-testid="confirm-email-input"
                  value={twoFaEmail}
                  onChange={(e) => setTwoFaEmail(e.target.value)}
                  placeholder={`name@${requiredDomain}`}
                  required
                  className="h-9"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="confirm-email-submit"
                className="w-full h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
                {loading ? 'Please wait...' : 'Send code'}
              </Button>
              <button type="button" onClick={resetToCredentials} className="w-full text-xs text-slate-500 hover:text-[#0F172A]">
                Back to login
              </button>
            </form>
          )}

          {isLogin && step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4" data-testid="otp-form">
              <p className="text-sm text-slate-600 flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                Enter the 6-digit code sent to <strong>{maskedEmail}</strong>.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Verification code</Label>
                <Input
                  data-testid="otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  className="h-11 text-center text-2xl tracking-[0.5em] font-semibold"
                />
              </div>
              <div className="text-xs text-center" data-testid="otp-timer">
                {otpSecondsLeft > 0 ? (
                  <span className="text-slate-500">Code expires in <strong className="font-mono text-[#0F172A]" data-testid="otp-countdown">{Math.floor(otpSecondsLeft / 60)}:{String(otpSecondsLeft % 60).padStart(2, '0')}</strong></span>
                ) : (
                  <span className="text-red-600" data-testid="otp-expired">Code expired — please request a new one.</span>
                )}
              </div>
              <Button type="submit" disabled={loading || otp.length < 6 || otpSecondsLeft <= 0} data-testid="otp-submit"
                className="w-full h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              <div className="flex items-center justify-between">
                <button type="button" onClick={resetToCredentials} className="text-xs text-slate-500 hover:text-[#0F172A]">
                  Back to login
                </button>
                <button type="button" onClick={handleResendOtp} data-testid="otp-resend" className="text-xs text-indigo-600 hover:text-indigo-800">
                  Resend code
                </button>
              </div>
            </form>
          )}

          {isLogin && step === 'forgot_request' && (
          <form onSubmit={handleForgotRequest} className="space-y-4" data-testid="forgot-request-form">
            <p className="text-xs text-slate-600">Enter your account email and we&apos;ll send you a reset code.</p>
            <div className="space-y-1.5">
              <Label htmlFor="forgot-email" className="text-xs font-medium text-slate-600">Email</Label>
              <Input id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com" required className="h-9" data-testid="forgot-email-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="forgot-request-btn">
              {loading ? 'Sending...' : 'Send Reset Code'}
            </Button>
            <button type="button" onClick={backToLogin} className="w-full text-xs text-slate-500 hover:text-slate-700" data-testid="forgot-back-btn">
              Back to Sign In
            </button>
          </form>
          )}

          {isLogin && step === 'forgot_reset' && (
          <form onSubmit={handleReset} className="space-y-4" data-testid="forgot-reset-form">
            <p className="text-xs text-slate-600">Enter the code sent to <strong>{maskedEmail || 'your email'}</strong> and choose a new password. The code expires in 10 minutes.</p>
            <div className="space-y-1.5">
              <Label htmlFor="reset-code" className="text-xs font-medium text-slate-600">Reset Code</Label>
              <Input id="reset-code" value={resetCode} onChange={(e) => setResetCode(e.target.value)}
                placeholder="6-digit code" maxLength={6} required className="h-9 tracking-widest text-center font-mono" data-testid="reset-code-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reset-pw1" className="text-xs font-medium text-slate-600">New Password</Label>
              <Input id="reset-pw1" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters" required className="h-9" data-testid="reset-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reset-pw2" className="text-xs font-medium text-slate-600">Confirm New Password</Label>
              <Input id="reset-pw2" type={showPassword ? 'text' : 'password'} value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)}
                placeholder="Re-enter new password" required className="h-9" data-testid="reset-password2-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold" data-testid="reset-submit-btn">
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={() => setStep('forgot_request')} className="text-slate-500 hover:text-slate-700" data-testid="reset-resend-btn">Resend code</button>
              <button type="button" onClick={backToLogin} className="text-slate-500 hover:text-slate-700">Back to Sign In</button>
            </div>
          </form>
          )}


          {(!isLogin || step === 'credentials') && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs font-medium text-slate-600">Full Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  required={!isLogin}
                  className="h-9"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-slate-600">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                required
                className="h-9"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-slate-600">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  className="h-9 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {isLogin && (
                <div className="text-right">
                  <button type="button" onClick={openForgot}
                    className="text-[11px] text-amber-700 hover:text-amber-800 hover:underline"
                    data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
              )}
            </div>
            {isLogin && showCaptcha && (
              <div className="space-y-1.5" data-testid="captcha-section">
                <Label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-slate-500" /> Security check — enter the characters shown
                </Label>
                <div className="flex items-center gap-2">
                  {captcha?.image ? (
                    <img src={captcha.image} alt="CAPTCHA" className="h-14 rounded border border-slate-200 bg-slate-100" data-testid="captcha-image" />
                  ) : (
                    <div className="h-14 w-40 rounded border border-slate-200 bg-slate-100 animate-pulse" />
                  )}
                  <button
                    type="button"
                    onClick={loadCaptcha}
                    disabled={captchaLoading}
                    className="text-slate-400 hover:text-slate-700 p-1"
                    title="Refresh CAPTCHA"
                    data-testid="captcha-refresh"
                  >
                    <RefreshCw className={`w-4 h-4 ${captchaLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <Input
                  data-testid="captcha-input"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Enter the code above"
                  autoComplete="off"
                  required
                  className="h-9 tracking-widest uppercase"
                />
              </div>
            )}
            <Button
              type="submit"
              className="w-full h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
          )}
          
          {/* Error Details Display */}
          {errorDetails && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="error-details">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-700 font-medium">{errorDetails.message}</p>
                  <button
                    type="button"
                    onClick={() => setShowDebug(!showDebug)}
                    className="text-xs text-red-500 hover:text-red-700 mt-1 underline"
                  >
                    {showDebug ? 'Hide' : 'Show'} technical details
                  </button>
                  {showDebug && (
                    <pre className="mt-2 text-[10px] text-red-600 bg-red-100 p-2 rounded overflow-x-auto">
                      {JSON.stringify(errorDetails.debug, null, 2)}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {step === 'credentials' && (
          <>
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => { setIsLogin(!isLogin); setShowCaptcha(false); setCaptcha(null); setCaptchaAnswer(''); setErrorDetails(null); }}
              className="text-sm text-slate-500 hover:text-[#0F172A] transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
            </button>
          </div>
          <p className="text-[10px] text-slate-400 text-center mt-4">
            BHPL-ERP/SM | First registered user receives Admin privileges
          </p>
          {pendingMessage && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="pending-message">
              <p className="text-xs text-amber-800 font-medium text-center">{pendingMessage}</p>
            </div>
          )}
          
          {/* Debug Info Toggle */}
          <div className="mt-4 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => setShowDebug(!showDebug)}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 mx-auto"
            >
              <Info className="w-3 h-3" />
              API Debug Info
            </button>
            {showDebug && !errorDetails && (
              <pre className="mt-2 text-[9px] text-slate-500 bg-slate-100 p-2 rounded overflow-x-auto">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            )}
          </div>
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
